import * as __WEBPACK_EXTERNAL_MODULE_react_jsx_runtime_225474f2__ from "react/jsx-runtime";
import * as __WEBPACK_EXTERNAL_MODULE_react__ from "react";
import * as __WEBPACK_EXTERNAL_MODULE__radix_ui_react_slot_85c2da36__ from "@radix-ui/react-slot";
import * as __WEBPACK_EXTERNAL_MODULE_class_variance_authority_159ad65c__ from "class-variance-authority";
import * as __WEBPACK_EXTERNAL_MODULE__lib_utils_mjs_6a3f3df3__ from "../lib/utils.mjs";
import * as __WEBPACK_EXTERNAL_MODULE__spinner_mjs_3c9b11bb__ from "./spinner.mjs";
import * as __WEBPACK_EXTERNAL_MODULE__internal_DynamicLink_mjs_d5a03e92__ from "../internal/DynamicLink.mjs";
import * as __WEBPACK_EXTERNAL_MODULE__hooks_use_keyboard_shortcut_mjs_75bbbe7f__ from "../hooks/use-keyboard-shortcut.mjs";
import * as __WEBPACK_EXTERNAL_MODULE__tooltip_mjs_e6c527ba__ from "./tooltip.mjs";
import * as __WEBPACK_EXTERNAL_MODULE__hooks_use_window_size_mjs_0c5b66fd__ from "../hooks/use-window-size.mjs";
import * as __WEBPACK_EXTERNAL_MODULE__lib_SIcon_mjs_c59156f3__ from "../lib/SIcon.mjs";
const buttonVariants = (0, __WEBPACK_EXTERNAL_MODULE_class_variance_authority_159ad65c__.cva)("inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:outline-none aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive cursor-pointer active:scale-[0.96] transform-gpu ring-0", {
    variants: {
        variant: {
            default: "bg-accent-light text-grayscale-darkest shadow-xs hover:bg-accent-light/90",
            secondary: "bg-[rgba(116,255,241,0.1)] text-brand-primary shadow-xs hover:bg-[rgba(116,255,241,0.15)]",
            tertiary: "bg-[rgba(163,163,163,0.1)] text-white hover:text-white/70 shadow-xs hover:bg-[rgba(163,163,163,0.15)]",
            destructive: "bg-alert-background/50 text-alert-primary shadow-xs hover:bg-alert-background/60 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40",
            outline: "border bg-grayscale-dark border-white/10 shadow-xs hover:bg-accent hover:text-accent-foreground text-white hover:text-white/50 !font-normal !text-[13px]",
            ghost: "hover:bg-grayscale-dark hover:text-foreground text-foreground/70",
            ghost_primary: "hover:bg-grayscale-dark hover:text-brand-primary text-brand-primary",
            ghost_destructive: "hover:bg-alert-background/50 hover:text-alert-primary text-foreground/70",
            link: "text-secondary-foreground underline-offset-2 hover:underline hover:text-brand-primary",
            dropdown: "border bg-grayscale-dark border-white/10 shadow-xs hover:bg-accent hover:text-accent-foreground text-white hover:text-white/50 !font-normal !text-[13px] active:scale-100",
            headless: ""
        },
        size: {
            default: "h-9 rounded-xl px-4 py-2 has-[>svg]:px-3",
            sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5 text-[11px]",
            lg: "h-10 rounded-xl px-6 has-[>svg]:px-4 text-base",
            xl: "h-11 px-8 text-lg",
            icon: "size-9",
            icon_lg: "size-10 rounded-xl",
            icon_sm: "size-8",
            icon_xs: "size-7"
        },
        isMinimal: {
            true: "!bg-transparent",
            false: ""
        }
    },
    defaultVariants: {
        variant: "default",
        size: "default",
        isMinimal: false
    }
});
const Button = /*#__PURE__*/ __WEBPACK_EXTERNAL_MODULE_react__.forwardRef(({ className, variant, size, isMinimal, asChild = false, onClick, onCommandClick, isLoading: _isLoading, href, linkProps, target, disabled = false, type = "button", children, hotkey, tooltip, disabledHint, disabledHintProps, ...props }, ref)=>{
    const [animateButtonPress, setAnimateButtonPress] = (0, __WEBPACK_EXTERNAL_MODULE_react__.useState)(false);
    const [didReturnPromise, setDidReturnPromise] = (0, __WEBPACK_EXTERNAL_MODULE_react__.useState)(null);
    const isLoading = _isLoading ?? didReturnPromise;
    const { isCommandPressed } = (0, __WEBPACK_EXTERNAL_MODULE__hooks_use_keyboard_shortcut_mjs_75bbbe7f__.useCommandKey)();
    const screenSize = (0, __WEBPACK_EXTERNAL_MODULE__hooks_use_window_size_mjs_0c5b66fd__["default"])();
    if (asChild) {
        const Comp = __WEBPACK_EXTERNAL_MODULE__radix_ui_react_slot_85c2da36__.Slot;
        return /*#__PURE__*/ (0, __WEBPACK_EXTERNAL_MODULE_react_jsx_runtime_225474f2__.jsx)(Comp, {
            ref: ref,
            "data-slot": "button",
            className: (0, __WEBPACK_EXTERNAL_MODULE__lib_utils_mjs_6a3f3df3__.cn)(buttonVariants({
                variant,
                size,
                isMinimal,
                className
            })),
            ...props,
            children: children
        });
    }
    const handleClick = async (e)=>{
        if (disabled || isLoading) return;
        const onClickUsing = isCommandPressed && void 0 !== onCommandClick ? onCommandClick : onClick;
        if (onClickUsing) {
            if (didReturnPromise) return;
            const onClickResult = onClickUsing(e);
            if (onClickResult instanceof Promise) {
                if (e) e.preventDefault();
                setDidReturnPromise(true);
                await onClickResult;
                setDidReturnPromise(null);
            }
        }
    };
    (0, __WEBPACK_EXTERNAL_MODULE__hooks_use_keyboard_shortcut_mjs_75bbbe7f__["default"])({
        hotkey,
        action: async ()=>{
            setAnimateButtonPress(true);
            await handleClick(void 0);
            setTimeout(()=>{
                setAnimateButtonPress(false);
            }, 100);
        }
    });
    if (void 0 !== href) return /*#__PURE__*/ (0, __WEBPACK_EXTERNAL_MODULE_react_jsx_runtime_225474f2__.jsx)(__WEBPACK_EXTERNAL_MODULE__internal_DynamicLink_mjs_d5a03e92__.DynamicLink, {
        href: href,
        ...linkProps,
        className: (0, __WEBPACK_EXTERNAL_MODULE__lib_utils_mjs_6a3f3df3__.cn)(buttonVariants({
            variant,
            size,
            isMinimal,
            className
        })),
        onClick: onClick,
        target: target,
        children: children
    });
    const Comp = "button";
    const buttonContent = /*#__PURE__*/ (0, __WEBPACK_EXTERNAL_MODULE_react_jsx_runtime_225474f2__.jsx)(Comp, {
        ref: ref,
        "data-slot": "button",
        className: (0, __WEBPACK_EXTERNAL_MODULE__lib_utils_mjs_6a3f3df3__.cn)(buttonVariants({
            variant,
            size,
            isMinimal,
            className
        })),
        disabled: disabled || !!isLoading,
        type: type,
        onClick: handleClick,
        ...props,
        children: /*#__PURE__*/ (0, __WEBPACK_EXTERNAL_MODULE_react_jsx_runtime_225474f2__.jsxs)("div", {
            className: (0, __WEBPACK_EXTERNAL_MODULE__lib_utils_mjs_6a3f3df3__.cn)("relative w-full", "dropdown" === variant && "flex flex-row gap-2 justify-between items-center"),
            children: [
                /*#__PURE__*/ (0, __WEBPACK_EXTERNAL_MODULE_react_jsx_runtime_225474f2__.jsx)("div", {
                    style: {
                        opacity: isLoading && "dropdown" !== variant ? 0 : 1,
                        transform: isLoading && "dropdown" !== variant ? "scale(0)" : animateButtonPress ? "scale(0.9)" : "scale(1.0)",
                        transition: "transform 0.1s ease-in-out, opacity 0.1s ease-in-out"
                    },
                    className: (0, __WEBPACK_EXTERNAL_MODULE__lib_utils_mjs_6a3f3df3__.cn)("flex flex-row gap-2 justify-center items-center"),
                    children: children
                }),
                "dropdown" === variant ? isLoading ? /*#__PURE__*/ (0, __WEBPACK_EXTERNAL_MODULE_react_jsx_runtime_225474f2__.jsx)(__WEBPACK_EXTERNAL_MODULE__lib_SIcon_mjs_c59156f3__.SIcon, {
                    type: "spinner",
                    className: "!size-3 shrink-0 opacity-60 animate-spin"
                }) : /*#__PURE__*/ (0, __WEBPACK_EXTERNAL_MODULE_react_jsx_runtime_225474f2__.jsx)(__WEBPACK_EXTERNAL_MODULE__lib_SIcon_mjs_c59156f3__.SIcon, {
                    type: "chevron-down",
                    className: "!size-3 shrink-0 opacity-50"
                }) : isLoading ? /*#__PURE__*/ (0, __WEBPACK_EXTERNAL_MODULE_react_jsx_runtime_225474f2__.jsx)("div", {
                    style: {
                        position: "absolute",
                        pointerEvents: "none",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        opacity: 1,
                        transform: "scale(1.0)",
                        transition: "transform 0.2s ease-in-out, opacity 0.2s ease-in-out"
                    },
                    children: /*#__PURE__*/ (0, __WEBPACK_EXTERNAL_MODULE_react_jsx_runtime_225474f2__.jsx)(__WEBPACK_EXTERNAL_MODULE__spinner_mjs_3c9b11bb__.SOverlaySpinner, {
                        overlayColor: "rgba(0,0,0,0)"
                    })
                }) : null
            ]
        })
    });
    const showTooltip = (hotkey || tooltip) && !tooltip?.disabled && (screenSize?.width || 100) > 768 || !!(disabled && disabledHint);
    return showTooltip ? /*#__PURE__*/ (0, __WEBPACK_EXTERNAL_MODULE_react_jsx_runtime_225474f2__.jsxs)(__WEBPACK_EXTERNAL_MODULE__tooltip_mjs_e6c527ba__.Tooltip, {
        children: [
            /*#__PURE__*/ (0, __WEBPACK_EXTERNAL_MODULE_react_jsx_runtime_225474f2__.jsx)(__WEBPACK_EXTERNAL_MODULE__tooltip_mjs_e6c527ba__.TooltipTrigger, {
                asChild: true,
                children: disabled && disabledHint ? /*#__PURE__*/ (0, __WEBPACK_EXTERNAL_MODULE_react_jsx_runtime_225474f2__.jsx)("span", {
                    className: "inline-flex",
                    style: {
                        pointerEvents: "auto"
                    },
                    children: buttonContent
                }) : buttonContent
            }),
            /*#__PURE__*/ (0, __WEBPACK_EXTERNAL_MODULE_react_jsx_runtime_225474f2__.jsx)(__WEBPACK_EXTERNAL_MODULE__tooltip_mjs_e6c527ba__.TooltipContent, {
                ...disabled && disabledHint ? disabledHintProps : {},
                children: /*#__PURE__*/ (0, __WEBPACK_EXTERNAL_MODULE_react_jsx_runtime_225474f2__.jsx)("div", {
                    className: "!max-w-[200px] inline",
                    children: /*#__PURE__*/ (0, __WEBPACK_EXTERNAL_MODULE_react_jsx_runtime_225474f2__.jsx)("div", {
                        className: "-my-3 -mx-1 inline-flex items-center justify-center gap-3",
                        children: disabled && disabledHint ? "string" == typeof disabledHint ? /*#__PURE__*/ (0, __WEBPACK_EXTERNAL_MODULE_react_jsx_runtime_225474f2__.jsx)("div", {
                            className: "text-sm font-normal pl-1",
                            children: disabledHint
                        }) : disabledHint : /*#__PURE__*/ (0, __WEBPACK_EXTERNAL_MODULE_react_jsx_runtime_225474f2__.jsxs)(__WEBPACK_EXTERNAL_MODULE_react_jsx_runtime_225474f2__.Fragment, {
                            children: [
                                tooltip?.content && ("string" == typeof tooltip.content ? /*#__PURE__*/ (0, __WEBPACK_EXTERNAL_MODULE_react_jsx_runtime_225474f2__.jsx)("div", {
                                    className: "text-sm font-normal pl-1",
                                    children: tooltip.content
                                }) : tooltip.content),
                                hotkey && /*#__PURE__*/ (0, __WEBPACK_EXTERNAL_MODULE_react_jsx_runtime_225474f2__.jsx)("div", {
                                    className: "flex flex-row items-center justify-center gap-1",
                                    children: (0, __WEBPACK_EXTERNAL_MODULE__hooks_use_keyboard_shortcut_mjs_75bbbe7f__.formatKeyboardShortcut)(hotkey).map((key, index)=>/*#__PURE__*/ (0, __WEBPACK_EXTERNAL_MODULE_react_jsx_runtime_225474f2__.jsx)("div", {
                                            className: "text-white text-xs bg-white/10 border border-white/10 font-medium tracking-wider rounded-md flex flex-row items-center justify-center w-[22px] h-[22px]",
                                            children: 0 === index ? /*#__PURE__*/ (0, __WEBPACK_EXTERNAL_MODULE_react_jsx_runtime_225474f2__.jsx)("span", {
                                                children: key
                                            }) : /*#__PURE__*/ (0, __WEBPACK_EXTERNAL_MODULE_react_jsx_runtime_225474f2__.jsx)("span", {
                                                className: "font-mono",
                                                children: key
                                            })
                                        }, index))
                                })
                            ]
                        })
                    })
                })
            })
        ]
    }) : buttonContent;
});
Button.displayName = "Button";
export { Button, buttonVariants };
